import { inject, Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { LottoV2Query } from '../../lotto/lotto-v2/lotto-item.query';
import { LottoFacade } from '../../lotto/lotto-v2/lotto.facade';
import { Lotto } from '../../lotto/lotto-v2/models/lotto.model';
import { LottoQuery } from '../../lotto/lotto.query';
import { LottoService } from '../../lotto/lotto.service';
import { ProcessProviderId } from '../../marketplace/models/process-provider-id';
import { LoyaltyStatusService } from '../loyalty-status.service';
import { MixedLoyaltyItem } from '../models/mixed-loyalty-item.model';
import { LoyaltySource } from './loyalty-source.model';
import { mapRewardStatusToLoyaltyStatus } from './reward-status.mapper';

/**
 * Lotto sources for `MixedLoyaltyQuery`, one per version. Registered against `LOTTO_SOURCE` with
 * `provideVersionedService`, so `componentVersions.lotto` selects which is used.
 */

/** v1: the Akita-backed lotto query, with status derived from item flags. */
@Injectable({ providedIn: 'root' })
export class LottoV1Source implements LoyaltySource {
  private lottoQuery = inject(LottoQuery);
  private lottoService = inject(LottoService);
  private statusService = inject(LoyaltyStatusService);

  fetch(): Observable<null> {
    return this.lottoService.fetchLottos();
  }

  selectFavourites(_showSoldOut: boolean): Observable<MixedLoyaltyItem[]> {
    return this.lottoQuery
      .selectFavourites()
      .pipe(
        map((lottos) =>
          lottos.map((x) => new MixedLoyaltyItem({ type: 'lotto', data: x, status: this.statusService.getStatus(x) }))
        )
      );
  }

  selectAll(_filterTypes: ProcessProviderId[]): Observable<MixedLoyaltyItem[]> {
    return this.lottoQuery
      .selectLottos()
      .pipe(
        map((lottos) =>
          lottos.map((x) => new MixedLoyaltyItem({ type: 'lotto', data: x, status: this.statusService.getStatus(x) }))
        )
      );
  }
}

/** v2: the signal-store query and facade, with REWARD_STATUS mapped to a state. */
@Injectable({ providedIn: 'root' })
export class LottoV2Source implements LoyaltySource {
  private lottoV2Query = inject(LottoV2Query);
  private lottoFacade = inject(LottoFacade);

  /** The facade's rxMethod is fire-and-forget, so this completes immediately. */
  fetch(): Observable<null> {
    this.lottoFacade.fetchLottos();
    return of(null);
  }

  selectFavourites(_showSoldOut: boolean): Observable<MixedLoyaltyItem[]> {
    return this.lottoV2Query
      .selectFavourites()
      .pipe(
        map((lottos: Lotto[]) =>
          (lottos ?? []).map(
            (x) => new MixedLoyaltyItem({ type: 'lotto', data: x, status: mapRewardStatusToLoyaltyStatus(x.Status) })
          )
        )
      );
  }

  selectAll(_filterTypes: ProcessProviderId[]): Observable<MixedLoyaltyItem[]> {
    return this.lottoV2Query
      .selectLottos()
      .pipe(
        map((lottos: Lotto[]) =>
          (lottos ?? []).map(
            (x) => new MixedLoyaltyItem({ type: 'lotto', data: x, status: mapRewardStatusToLoyaltyStatus(x.Status) })
          )
        )
      );
  }
}
